import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Badge,
  Spinner,
  Alert,
  Table,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  OverlayTrigger,
  Tooltip,
  Tab,
  Tabs,
} from "react-bootstrap";
import MoonLoader from "react-spinners/MoonLoader";
import {
  IconCreditCard,
  IconSearch,
  IconFilter,
  IconEye,
  IconTrash,
  IconAlertTriangle,
  IconPlus,
  IconTrendingUp,
  IconCalendar,
  IconCheck,
  IconClock,
  IconUsers,
  IconRobot,
  IconBuildingStore,
  IconPuzzle,
  IconFileInvoice,
  IconExternalLink,
  IconReceipt,
  IconCash,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import CustomerSubscriptionServices from "../../services/CustomerSubscriptionServices";
import UserContext from "../../contexts/UserContext";
import SubscriptionPaymentModal from "../Organization/modals/SubscriptionPaymentModal";
import SubscriptionDetailsModal from "../../jsx/components/Modals/SubscriptionDetailsModal";
import "./UserSubscriptionsList.css";

const UserSubscriptionsList = () => {
  const { isOrganization, user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [cancelingSubscriptionId, setCancelingSubscriptionId] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recurrencyFilter, setRecurrencyFilter] = useState("");
  const [activeOnlyFilter, setActiveOnlyFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Estados para modal de pagamento
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Estados para modal de detalhes da assinatura
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);

  // Estado da tab ativa
  const [activeTab, setActiveTab] = useState("plan");

  // Estados para aba "Minha Assinatura"
  const [planLoading, setPlanLoading] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("");
  const [invoiceOffset, setInvoiceOffset] = useState(0);
  const [invoiceHasMore, setInvoiceHasMore] = useState(false);
  const [invoiceTotalCount, setInvoiceTotalCount] = useState(0);
  const invoicePageSize = 10;

  useEffect(() => {
    if (isOrganization() && user?.superuser_level <= 3) {
      loadPlanInfo();
      loadInvoices();
    }
  }, [user, invoiceStatusFilter, invoiceOffset]);

  useEffect(() => {
    if (isOrganization() && user?.superuser_level <= 3) {
      loadPaymentSummary();
      loadSubscriptions();
    }
  }, [
    user,
    currentPage,
    searchTerm,
    statusFilter,
    recurrencyFilter,
    activeOnlyFilter,
  ]);

  const loadPaymentSummary = async () => {
    try {
      setSummaryLoading(true);
      const response = await CustomerSubscriptionServices.getPaymentSummary({
        type: "contracts"
      });
      setPaymentSummary(response.data || {});
    } catch (error) {
      console.error("Erro ao carregar resumo de pagamentos:", error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadPlanInfo = async () => {
    try {
      setPlanLoading(true);
      const response = await CustomerSubscriptionServices.getPlanSubscriptionInfo();
      setPlanInfo(response.data?.subscription || null);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Erro ao carregar info do plano:", error);
      }
      setPlanInfo(null);
    } finally {
      setPlanLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const params = {
        offset: invoiceOffset,
        limit: invoicePageSize,
      };
      if (invoiceStatusFilter) {
        params.payment_status = invoiceStatusFilter;
      }
      const response = await CustomerSubscriptionServices.getPlanInvoices(params);
      const data = response.data;
      setInvoices(data?.invoices || []);
      setInvoiceHasMore(data?.has_more || false);
      setInvoiceTotalCount(data?.total_count || 0);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Erro ao carregar faturas:", error);
      }
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    try {
      setLoading(true);

      const filters = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm,
        status: statusFilter,
        recurrency: recurrencyFilter,
        is_active_subscription: activeOnlyFilter,
        ordering: "-created_at",
        type: "contracts",
      };

      const params = CustomerSubscriptionServices.buildSearchParams(filters);
      const response = await CustomerSubscriptionServices.listSubscriptions(
        params
      );

      // Formatar dados para exibição
      const formattedSubscriptions =
        response.data.results?.map((subscription) =>
          CustomerSubscriptionServices.formatSubscriptionData(subscription)
        ) || [];

      setSubscriptions(formattedSubscriptions);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error("Erro ao carregar assinaturas:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar as assinaturas.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleRecurrencyFilter = (recurrency) => {
    setRecurrencyFilter(recurrency);
    setCurrentPage(1);
  };

  const handleActiveOnlyFilter = (activeOnly) => {
    setActiveOnlyFilter(activeOnly);
    setCurrentPage(1);
  };

  // Verificar permissões
  const canViewSubscription = (subscription) => {
    return CustomerSubscriptionServices.canPerformAction(
      "view",
      { isOrganization, user },
      subscription
    );
  };

  const canCancelSubscription = (subscription) => {
    return CustomerSubscriptionServices.canPerformAction(
      "cancel",
      { isOrganization, user },
      subscription
    );
  };

  // Handlers para ações
  const handleContractAgents = () => {
    const paymentInfo = {
      value: 150, // Valor padrão por agente adicional - pode vir de configuração
      description: "Assinatura subconta adicional",
    };

    setPaymentData(paymentInfo);
    setShowPaymentModal(true);
  };

  const handleViewSubscription = (subscriptionId) => {
    setSelectedSubscriptionId(subscriptionId);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedSubscriptionId(null);
  };

  const handleCancelSubscription = async (subscription) => {
    if (!canCancelSubscription(subscription)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para cancelar assinaturas.",
      });
      return;
    }

    // Primeiro modal: confirmação e solicitação do motivo
    const { value: reason, isConfirmed } = await Swal.fire({
      title: "Confirmar Cancelamento",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Assinatura:</strong> #${
            subscription.subscription_id || subscription.id
          }</p>
          <p><strong>Cliente:</strong> ${
            subscription.agent_company_name || "N/A"
          }</p>
          <p><strong>Valor:</strong> ${subscription.formattedValue}</p>
          <hr>
          <p class="text-warning"><strong>⚠️ Atenção:</strong></p>
          <p>O cancelamento desta assinatura irá <strong>reduzir o limite de agentes</strong> que você pode criar.</p>
          <hr>
          <label for="cancellation-reason" style="display: block; margin-bottom: 8px; font-weight: bold;">
            Motivo do cancelamento:
          </label>
        </div>
      `,
      input: "textarea",
      inputPlaceholder: "Informe o motivo do cancelamento...",
      inputAttributes: {
        id: "cancellation-reason",
        maxlength: 500,
        rows: 3,
      },
      inputValidator: (value) => {
        if (!value || value.trim().length < 5) {
          return "Por favor, informe um motivo com pelo menos 5 caracteres!";
        }
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, cancelar assinatura",
      cancelButtonText: "Não cancelar",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      width: 650,
      allowOutsideClick: false,
    });

    if (!isConfirmed || !reason) return;

    try {
      setCancelingSubscriptionId(
        subscription.subscription_id || subscription.id
      );

      // Chamada para cancelamento com internal=true e motivo
      await CustomerSubscriptionServices.cancelSubscription(
        subscription.subscription_id || subscription.id,
        reason.trim(),
        true
      );

      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Assinatura cancelada com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadSubscriptions();
      loadPaymentSummary();
    } catch (error) {
      console.error("Erro ao cancelar assinatura:", error);
      const errorInfo =
        CustomerSubscriptionServices.processSubscriptionError(error);

      // Tratamento específico para erro de agente implementado
      if (errorInfo.type === "agent_deployed") {
        Swal.fire({
          icon: "error",
          title: "Cancelamento Bloqueado",
          html: `
            <div style="text-align: left; padding: 10px;">
              <p><strong>Agente:</strong> ${errorInfo.details.agent_name}</p>
              <hr>
              <p class="text-danger">${errorInfo.message}</p>
            </div>
          `,
          confirmButtonColor: "#dc3545",
          width: 600,
        });
      } else if (errorInfo.type === "already_canceled") {
        Swal.fire({
          icon: "info",
          title: "Assinatura Já Cancelada",
          text: errorInfo.message,
          confirmButtonColor: "#17a2b8",
        }).then(() => {
          // Recarregar dados para refletir o status atualizado
          loadSubscriptions();
          loadPaymentSummary();
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: errorInfo.message,
          confirmButtonColor: "#dc3545",
        });
      }
    } finally {
      setCancelingSubscriptionId(null);
    }
  };

  const handlePaymentSuccess = (subscriptionResult) => {
    Swal.fire({
      icon: "success",
      title: "🎉 Assinatura Criada!",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Quantidade de Subcontas:</strong> ${
            paymentData?.additional_agents || 1
          }</p>
          <p><strong>Valor:</strong> ${subscriptionResult.formattedValue}</p>
          <p><strong>Próximo vencimento:</strong> ${
            subscriptionResult.formattedNextDueDate
          }</p>
          <hr>
          <p class="text-muted">Sua assinatura está ativa e a subconta adicional foi adicionada em seu plano.</p>
        </div>
      `,
      confirmButtonText: "Continuar",
      confirmButtonColor: "#28a745",
    });

    setShowPaymentModal(false);
    setPaymentData(null);
    loadSubscriptions();
    loadPaymentSummary();
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setPaymentData(null);
  };

  // ===== RENDERIZAÇÃO DA ABA "MINHA ASSINATURA" =====

  const renderPlanTab = () => {
    if (planLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando informações do plano...</p>
          </div>
        </div>
      );
    }

    if (!planInfo) {
      return (
        <Alert variant="info" className="text-center mt-4">
          <IconFileInvoice size={24} className="mb-2" />
          <p className="mb-0">Nenhuma assinatura de plano encontrada.</p>
          <small>Quando você contratar um plano, as informações aparecerão aqui.</small>
        </Alert>
      );
    }

    return (
      <div className="plan-tab-content">
        {/* Card de Resumo do Plano */}
        <Card className="mb-4 mt-3 plan-info-card">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center mb-2">
                  <IconCreditCard size={24} className="me-2 text-primary" />
                  <h5 className="mb-0">Assinatura do Plano</h5>
                  <Badge
                    bg={planInfo.asaas_status === "ACTIVE" ? "success" : "secondary"}
                    className="ms-2"
                  >
                    {planInfo.asaas_status_description || (planInfo.is_active ? "Ativo" : "Inativo")}
                  </Badge>
                </div>
                {planInfo.description && (
                  <p className="text-muted mb-1">{planInfo.description}</p>
                )}
                <div className="plan-details-grid mt-3">
                  <div className="plan-detail-item">
                    <small className="text-muted">Valor</small>
                    <strong>{CustomerSubscriptionServices.formatCurrency(planInfo.value)}</strong>
                  </div>
                  <div className="plan-detail-item">
                    <small className="text-muted">Recorrência</small>
                    <strong>{planInfo.cycle_description || CustomerSubscriptionServices.getRecurrencyText(planInfo.recurrency)}</strong>
                  </div>
                  <div className="plan-detail-item">
                    <small className="text-muted">Próximo Vencimento</small>
                    <strong>{CustomerSubscriptionServices.formatDate(planInfo.next_due_date)}</strong>
                  </div>
                  <div className="plan-detail-item">
                    <small className="text-muted">Forma de Pagamento</small>
                    <strong>
                      {planInfo.operation_type === "CREDIT_CARD" ? "Cartão de Crédito" :
                       planInfo.operation_type === "PIX" ? "PIX" :
                       planInfo.operation_type === "BOLETO" ? "Boleto" :
                       planInfo.operation_type || "—"}
                      {planInfo.card_last_digits ? ` (****${planInfo.card_last_digits})` : ""}
                    </strong>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleViewSubscription(planInfo.subscription_id)}
                >
                  <IconEye size={16} className="me-1" />
                  Ver Detalhes
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Filtros de Faturas */}
        <Card className="mb-3">
          <Card.Body className="py-2">
            <Row className="align-items-center">
              <Col xs={12} md={4}>
                <InputGroup size="sm">
                  <InputGroup.Text>
                    <IconFilter size={14} />
                  </InputGroup.Text>
                  <Form.Select
                    value={invoiceStatusFilter}
                    onChange={(e) => {
                      setInvoiceStatusFilter(e.target.value);
                      setInvoiceOffset(0);
                    }}
                    size="sm"
                  >
                    <option value="">Todas as Faturas</option>
                    <option value="PENDING">Pendentes</option>
                    <option value="CONFIRMED">Pagas</option>
                    <option value="RECEIVED">Recebidas</option>
                    <option value="OVERDUE">Vencidas</option>
                    <option value="REFUNDED">Estornadas</option>
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col xs={12} md={8} className="text-end">
                <small className="text-muted">
                  {invoiceTotalCount} fatura{invoiceTotalCount !== 1 ? "s" : ""} encontrada{invoiceTotalCount !== 1 ? "s" : ""}
                </small>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tabela de Faturas */}
        <Card>
          <Card.Header className="d-flex gap-2 align-items-center">
            <IconReceipt size={20} />
            <h5 className="mb-0">Faturas</h5>
          </Card.Header>
          <Card.Body>
            {invoicesLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "150px" }}>
                <Spinner animation="border" variant="primary" size="sm" />
                <span className="ms-2">Carregando faturas...</span>
              </div>
            ) : invoices.length > 0 ? (
              <>
                <Table responsive hover className="invoices-table">
                  <thead>
                    <tr>
                      <th>Vencimento</th>
                      <th>Valor</th>
                      <th>Forma Pgto.</th>
                      <th>Status</th>
                      <th>Data Pagamento</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <IconCalendar size={14} className="me-1 text-muted" />
                            {CustomerSubscriptionServices.formatDate(invoice.due_date)}
                          </div>
                        </td>
                        <td>
                          <strong>
                            {CustomerSubscriptionServices.formatCurrency(invoice.value)}
                          </strong>
                        </td>
                        <td>
                          <small>{invoice.billing_type_description || invoice.billing_type}</small>
                        </td>
                        <td>
                          <Badge bg={CustomerSubscriptionServices.getInvoiceStatusVariant(invoice.status)}>
                            {CustomerSubscriptionServices.getInvoiceStatusText(invoice.status)}
                          </Badge>
                        </td>
                        <td>
                          {invoice.payment_date
                            ? CustomerSubscriptionServices.formatDate(invoice.payment_date)
                            : "—"}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            {invoice.invoice_url && (
                              <OverlayTrigger overlay={<Tooltip>Ver fatura</Tooltip>}>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => window.open(invoice.invoice_url, "_blank")}
                                >
                                  <IconExternalLink size={14} />
                                </Button>
                              </OverlayTrigger>
                            )}
                            {invoice.status === "PENDING" && invoice.invoice_url && (
                              <OverlayTrigger overlay={<Tooltip>Pagar fatura</Tooltip>}>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => window.open(invoice.invoice_url, "_blank")}
                                >
                                  <IconCash size={14} />
                                </Button>
                              </OverlayTrigger>
                            )}
                            {invoice.status === "OVERDUE" && invoice.invoice_url && (
                              <OverlayTrigger overlay={<Tooltip>Regularizar pagamento</Tooltip>}>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => window.open(invoice.invoice_url, "_blank")}
                                >
                                  <IconCash size={14} />
                                </Button>
                              </OverlayTrigger>
                            )}
                            {invoice.bank_slip_url && (
                              <OverlayTrigger overlay={<Tooltip>Ver boleto</Tooltip>}>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => window.open(invoice.bank_slip_url, "_blank")}
                                >
                                  <IconFileInvoice size={14} />
                                </Button>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Paginação de Faturas */}
                {(invoiceOffset > 0 || invoiceHasMore) && (
                  <div className="d-flex justify-content-center align-items-center mt-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      disabled={invoiceOffset === 0}
                      onClick={() => setInvoiceOffset(Math.max(0, invoiceOffset - invoicePageSize))}
                    >
                      Anterior
                    </Button>
                    <small className="text-muted mx-2">
                      {invoiceOffset + 1} - {Math.min(invoiceOffset + invoicePageSize, invoiceTotalCount)} de {invoiceTotalCount}
                    </small>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="ms-2"
                      disabled={!invoiceHasMore}
                      onClick={() => setInvoiceOffset(invoiceOffset + invoicePageSize)}
                    >
                      Próximo
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Alert variant="info" className="text-center mb-0">
                <IconReceipt size={24} className="mb-2" />
                <p className="mb-0">
                  {invoiceStatusFilter
                    ? "Nenhuma fatura encontrada com o filtro selecionado."
                    : "Nenhuma fatura encontrada para esta assinatura."}
                </p>
              </Alert>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  // ===== RENDERIZAÇÃO DA ABA "CONTRATAÇÕES" =====

  const renderSummaryCards = () => {
    const cards = [
      {
        title: "Total de Pagamentos",
        value: paymentSummary.total_payments || 0,
        icon: <IconCreditCard size={24} />,
        variant: "primary",
      },
      {
        title: "Valor Total",
        value: CustomerSubscriptionServices.formatCurrency(
          paymentSummary.total_value || 0
        ),
        icon: <IconTrendingUp size={24} />,
        variant: "success",
      },
      {
        title: "Assinaturas Ativas",
        value: paymentSummary.active_subscriptions || 0,
        icon: <IconCheck size={24} />,
        variant: "info",
      },
      /* {
        title: "Pagamentos Confirmados",
        value: paymentSummary.confirmed_payments || 0,
        icon: <IconCalendar size={24} />,
        variant: "warning",
      }, */
    ];

    return (
      <Row className="mb-4">
        {cards.map((card, index) => (
          <Col lg={4} md={4} sm={6} xs={12} key={index} className="mb-3">
            <Card className="h-100">
              <Card.Body className="d-flex align-items-center">
                <div
                  className={`icon-wrapper bg-${card.variant} bg-gradient me-3`}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <h6 className="card-title mb-1 text-muted">{card.title}</h6>
                  <h4 className="mb-0 fw-bold">
                    {summaryLoading ? <Spinner size="sm" /> : card.value}
                  </h4>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "primary" : "outline-primary"}
          size="sm"
          className="me-1"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-center align-items-center mt-3">
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Anterior
        </Button>
        {pages}
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-2"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Próximo
        </Button>
      </div>
    );
  };

  // Ícone por tipo de contrato
  const getContractTypeIcon = (subscription) => {
    if (subscription.contract_type === 'agent') return <IconRobot size={16} className="me-1" />;
    if (subscription.subscription_type === 'subaccount') return <IconUsers size={16} className="me-1" />;
    if (subscription.subscription_type === 'marketplace_agent') return <IconBuildingStore size={16} className="me-1" />;
    return <IconPuzzle size={16} className="me-1" />;
  };

  // Descrição detalhada do contrato
  const getContractDescription = (subscription) => {
    if (subscription.contract_type === 'agent') {
      return subscription.feature_name || 'Agentes Publicados';
    }
    if (subscription.feature_name) {
      return subscription.feature_name;
    }
    if (subscription.subscription_type === 'subaccount') {
      return 'Subconta Adicional';
    }
    return subscription.subscription_type_display || '—';
  };

  // Obter filtros disponíveis
  const availableFilters = CustomerSubscriptionServices.getAvailableFilters();

  const renderContractsTab = () => {
    return (
      <div className="contracts-tab-content">
        <div className="page-header mb-4 mt-3">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              <p className="text-muted mb-0">
                Gerencie suas contratações de subcontas, agentes publicados e outros recursos adicionais.
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="success" onClick={handleContractAgents}>
                <IconPlus size={16} className="me-1" />
                Contratar Recurso
              </Button>
            </div>
          </div>
        </div>

        {/* Cards de Resumo */}
        {renderSummaryCards()}

        {/* Filtros e Busca */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col xs={12} md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <IconSearch size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por ID ou descrição..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col xs={12} md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <IconFilter size={16} />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                  >
                    {availableFilters.status.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col xs={12} md={3}>
                <Form.Select
                  value={recurrencyFilter}
                  onChange={(e) => handleRecurrencyFilter(e.target.value)}
                >
                  {availableFilters.recurrency.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={3}>
                <Form.Select
                  value={activeOnlyFilter}
                  onChange={(e) => handleActiveOnlyFilter(e.target.value)}
                >
                  {availableFilters.is_active_subscription.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Lista de Assinaturas */}
        <Card>
          <Card.Header className="d-flex gap-2 align-items-center">
            <IconCreditCard size={20} />
            <h5 className="mb-0">Lista de Contratações</h5>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "200px" }}
              >
                <div className="text-center">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Carregando assinaturas...</p>
                </div>
              </div>
            ) : subscriptions.length > 0 ? (
              <>
                <Table responsive hover className="subscriptions-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th>Qtd.</th>
                      <th>Valor</th>
                      <th>Recorrência</th>
                      <th>Próx. Vencimento</th>
                      <th>Status</th>
                      <th>Criado em</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((subscription) => (
                      <tr key={subscription.id}>
                        <td>
                          <Badge bg={subscription.contractTypeVariant} className="d-inline-flex align-items-center">
                            {getContractTypeIcon(subscription)}
                            {subscription.contractTypeLabel}
                          </Badge>
                        </td>
                        <td>
                          <div>
                            <div className="fw-bold">
                              {getContractDescription(subscription)}
                            </div>
                            {subscription.target_subaccount_id && (
                              <small className="text-muted">
                                Subconta #{subscription.target_subaccount_id}
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="fw-bold">{subscription.quantity || 1}</span>
                        </td>
                        <td>
                          <div className="fw-bold">
                            {subscription.formattedValue}
                          </div>
                        </td>
                        <td>
                          <Badge bg={subscription.recurrencyVariant}>
                            {subscription.recurrencyText}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <IconCalendar size={14} className="me-1 text-muted" />
                            {subscription.formattedNextDueDate}
                          </div>
                        </td>
                        <td>
                          <Badge bg={subscription.statusVariant}>
                            {subscription.statusText}
                          </Badge>
                        </td>
                        <td>
                          <span className="date-info">
                            {subscription.formattedCreatedAt}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            {subscription.status !== 6 && (
                              <OverlayTrigger
                                overlay={<Tooltip>Ver detalhes</Tooltip>}
                              >
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    handleViewSubscription(
                                      subscription.subscription_id
                                    )
                                  }
                                  disabled={!canViewSubscription(subscription)}
                                >
                                  <IconEye size={14} />
                                </Button>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {renderPagination()}
              </>
            ) : !isOrganization() ? (
              <Alert variant="warning" className="text-center">
                <IconAlertTriangle size={24} className="mb-2" />
                <p className="mb-0">
                  Esta funcionalidade está disponível apenas para organizações.
                </p>
              </Alert>
            ) : user?.superuser_level > 3 ? (
              <Alert variant="warning" className="text-center">
                <IconAlertTriangle size={24} className="mb-2" />
                <p className="mb-0">
                  Você não tem permissão para visualizar assinaturas.
                </p>
              </Alert>
            ) : (
              <Alert variant="info" className="text-center">
                <IconCreditCard size={24} className="mb-2" />
                <p className="mb-0">
                  {searchTerm ||
                  statusFilter ||
                  recurrencyFilter ||
                  activeOnlyFilter
                    ? "Nenhuma contratação encontrada com os filtros aplicados."
                    : "Nenhuma contratação encontrada."}
                </p>
                <small>
                  {searchTerm ||
                  statusFilter ||
                  recurrencyFilter ||
                  activeOnlyFilter
                    ? "Tente ajustar os filtros de busca."
                    : "Novas contratações de subcontas e agentes aparecerão aqui."}
                </small>
              </Alert>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <div className="user-subscriptions-container">
      <div className="page-header mb-3">
        <h3 className="d-flex align-items-center">
          <IconCreditCard size={30} className="me-2" />
          Assinaturas e Contratações
        </h3>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="subscriptions-page-tabs mb-0"
      >
        <Tab
          eventKey="plan"
          title={
            <span className="subscriptions-tab-title">
              <IconFileInvoice size={18} className="me-2" />
              Minha Assinatura
            </span>
          }
        >
          {renderPlanTab()}
        </Tab>
        <Tab
          eventKey="contracts"
          title={
            <span className="subscriptions-tab-title">
              <IconCreditCard size={18} className="me-2" />
              Contratações
            </span>
          }
        >
          {renderContractsTab()}
        </Tab>
      </Tabs>

      {/* Modal de Pagamento */}
      <SubscriptionPaymentModal
        show={showPaymentModal}
        onHide={handlePaymentCancel}
        paymentData={paymentData}
        onSuccess={handlePaymentSuccess}
      />

      {/* Modal de Detalhes da Assinatura */}
      <SubscriptionDetailsModal
        show={showDetailsModal}
        onHide={handleCloseDetailsModal}
        subscriptionId={selectedSubscriptionId}
      />

      {/* Overlay de Cancelamento */}
      {cancelingSubscriptionId && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "10px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              minWidth: "300px",
            }}
          >
            <MoonLoader size={50} color="var(--primary)" />
            <p
              style={{
                marginTop: "20px",
                marginBottom: "10px",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Cancelando assinatura...
            </p>
            <p style={{ margin: 0, fontSize: "14px", color: "#6c757d" }}>
              Por favor, aguarde um momento
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSubscriptionsList;
